import { 
  collection, doc, addDoc, getDoc, getDocs, updateDoc, deleteDoc, 
  query, where, serverTimestamp, setDoc 
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, updateProfile, reauthenticateWithCredential, updatePassword, signInWithPopup
} from 'firebase/auth';
import { db, auth, googleProvider } from './firebase';
import { deleteFile, uploadTemplateThumbnail } from './storage';
import { EmailAuthProvider } from 'firebase/auth';

// Export db directly so it can be imported by other files
export { db };

// User functions
export const userService = {
  async login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Get additional user data from Firestore
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      
      if (userDoc.exists()) {
        return {
          id: userCredential.user.uid,
          ...userDoc.data()
        };
      } else {
        throw new Error('User data not found');
      }
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  },
  
  async register(userData) {
    try {
      // Create authentication user
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        userData.email, 
        userData.password
      );
      
      // Check if this is the first user to register
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const isFirstUser = usersSnapshot.empty;
      
      // Save additional user data to Firestore
      const userRef = doc(db, 'users', userCredential.user.uid);
      await setDoc(userRef, {
        username: userData.username,
        email: userData.email,
        isAdmin: isFirstUser, // First user gets admin privileges
        created: serverTimestamp()
      });
      
      // Return user without password
      const { password, ...userWithoutPassword } = userData;
      return { 
        id: userCredential.user.uid, 
        isAdmin: isFirstUser,
        ...userWithoutPassword 
      };
    } catch (error) {
      console.error("Error registering user:", error);
      throw error;
    }
  },
  
  async logout() {
    return signOut(auth);
  },
  
  async getById(id) {
    try {
      const userDoc = await getDoc(doc(db, 'users', id));
      
      if (userDoc.exists()) {
        return {
          id: userDoc.id,
          ...userDoc.data()
        };
      }
      return null;
    } catch (error) {
      console.error("Error getting user:", error);
      throw error;
    }
  },
  
  async isAdmin(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      return userDoc.exists() && userDoc.data().isAdmin === true;
    } catch (error) {
      console.error("Error checking admin status:", error);
      return false;
    }
  },
  
  updateProfile: async (userId, userData) => {
    try {
      const userRef = doc(db, 'users', userId);
      
      // Update user document in Firestore
      await updateDoc(userRef, {
        fullName: userData.fullName,
        username: userData.username,
        phoneNumber: userData.phoneNumber,
        updatedAt: serverTimestamp()
      });
      
      // Update display name in Firebase Auth if provided
      if (userData.fullName) {
        const currentUser = auth.currentUser;
        if (currentUser) {
          await updateProfile(currentUser, {
            displayName: userData.fullName
          });
        }
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { error: error.message };
    }
  },
  
  changePassword: async (currentPassword, newPassword) => {
    try {
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Re-authenticate user before changing password
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );
      
      await reauthenticateWithCredential(user, credential);
      
      // Change password
      await updatePassword(user, newPassword);
      
      return { success: true };
    } catch (error) {
      console.error('Error changing password:', error);
      return { error: error.message };
    }
  },
  
  async signInWithGoogle() {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // Check if user document exists in Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        // Check if this is the first user to register
        const usersCollection = collection(db, 'users');
        const usersSnapshot = await getDocs(usersCollection);
        const isFirstUser = usersSnapshot.empty;
        
        // Create a new user document in Firestore
        await setDoc(doc(db, 'users', user.uid), {
          username: user.displayName || user.email.split('@')[0],
          email: user.email,
          created: serverTimestamp(),
          isAdmin: isFirstUser // First user gets admin privileges
        });
      }
      
      // Get the user data from Firestore
      const updatedUserDoc = await getDoc(doc(db, 'users', user.uid));
      
      return {
        id: user.uid,
        ...updatedUserDoc.data()
      };
    } catch (error) {
      console.error("Google Sign-In error:", error);
      throw error;
    }
  }
};

// Event functions
export const eventService = {
  async getAll(userId) {
    try {
      const eventsRef = collection(db, 'events');
      const q = query(eventsRef, where('userId', '==', userId));
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error("Error getting events:", error);
      throw error;
    }
  },
  
  async getById(id) {
    try {
      const eventDoc = await getDoc(doc(db, 'events', id));
      
      if (eventDoc.exists()) {
        return {
          id: eventDoc.id,
          ...eventDoc.data()
        };
      }
      return null;
    } catch (error) {
      console.error("Error getting event:", error);
      throw error;
    }
  },
  
  async create(eventData) {
    try {
      const eventRef = await addDoc(collection(db, 'events'), {
        ...eventData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return {
        id: eventRef.id,
        ...eventData
      };
    } catch (error) {
      console.error("Error creating event:", error);
      throw error;
    }
  },
  
  async update(id, eventData) {
    try {
      const eventRef = doc(db, 'events', id);
      
      await updateDoc(eventRef, {
        ...eventData,
        updatedAt: serverTimestamp()
      });
      
      return {
        id,
        ...eventData
      };
    } catch (error) {
      console.error("Error updating event:", error);
      throw error;
    }
  },
  
  async delete(id) {
    try {
      // First get all invitations for this event
      const invitationsSnapshot = await getDocs(
        query(collection(db, 'invitations'), where('eventId', '==', id))
      );
      
      // Delete all invitations
      const deleteInvitationPromises = invitationsSnapshot.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      
      // Get all guests for this event
      const guestsSnapshot = await getDocs(
        query(collection(db, 'guests'), where('eventId', '==', id))
      );
      
      // Delete all guests
      const deleteGuestPromises = guestsSnapshot.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      
      // Wait for all deletions to complete
      await Promise.all([...deleteInvitationPromises, ...deleteGuestPromises]);
      
      // Delete the event itself
      await deleteDoc(doc(db, 'events', id));
      
      return true;
    } catch (error) {
      console.error("Error deleting event:", error);
      throw error;
    }
  },

  async getInvitations(eventId) {
    try {
      console.log("🚨 DEBUG: getInvitations called with eventId:", eventId);
      
      // Ensure eventId is a string for consistent comparisons
      const eventIdStr = String(eventId);
      
      // Log the Firestore query we're about to execute
      console.log("🚨 DEBUG: Querying 'invitations' where eventId =", eventIdStr);
      
      // Get all invitations (temporary workaround to debug)
      const allInvitationsSnapshot = await getDocs(collection(db, 'invitations'));
      console.log("🚨 DEBUG: Total invitations in database:", allInvitationsSnapshot.docs.length);
      
      // Log all invitations to see what's stored
      allInvitationsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        console.log(`🚨 INVITATION: id=${doc.id}, name=${data.name}, eventId=${data.eventId}, type=${typeof data.eventId}`);
      });
      
      // Now do the actual filtered query
      const invitationsRef = collection(db, 'invitations');
      const q = query(invitationsRef, where('eventId', '==', eventIdStr));
      const querySnapshot = await getDocs(q);
      
      console.log(`🚨 DEBUG: Query returned ${querySnapshot.docs.length} invitations for eventId=${eventIdStr}`);
      
      // Convert query results to array of objects
      const invitations = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return invitations;
    } catch (error) {
      console.error("Error getting invitations:", error);
      throw error;
    }
  },
  
  async getGuests(eventId) {
    try {
      const guestsRef = collection(db, 'guests');
      const q = query(guestsRef, where('eventId', '==', eventId));
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error("Error getting guests:", error);
      throw error;
    }
  },

  async getAllEvents() {
    try {
      const querySnapshot = await getDocs(collection(db, 'events'));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error("Error getting all events:", error);
      throw error;
    }
  },

  createSlug(text) {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  },

  async getBySlug(slug) {
    try {
      const eventsRef = collection(db, 'events');
      const querySnapshot = await getDocs(eventsRef);
      
      // Filter events where slug matches
      const events = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return events.find(event => this.createSlug(event.name) === slug);
    } catch (error) {
      console.error("Error getting event by slug:", error);
      throw error;
    }
  }
};

// Invitation functions
export const invitationService = {
  async getByEventId(eventId) {
    try {
      console.log("🚨 invitationService.getByEventId called with:", eventId);
      
      // Always convert to string for consistency
      const eventIdStr = String(eventId);
      
      const invitationsRef = collection(db, 'invitations');
      const q = query(invitationsRef, where('eventId', '==', eventIdStr));
      
      console.log("🔍 Executing Firestore query for:", eventIdStr);
      const querySnapshot = await getDocs(q);
      
      console.log(`🔍 Found ${querySnapshot.docs.length} invitations`);
      
      // Map to array with IDs
      const invitations = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return invitations;
    } catch (error) {
      console.error("❌ Error in getByEventId:", error);
      throw error;
    }
  },
  
  async getById(id) {
    try {
      const invitationDoc = await getDoc(doc(db, 'invitations', id));
      
      if (invitationDoc.exists()) {
        return {
          id: invitationDoc.id,
          ...invitationDoc.data()
        };
      }
      return null;
    } catch (error) {
      console.error("Error getting invitation:", error);
      throw error;
    }
  },
  
  async create(invitationData) {
    try {
      console.log("🔍 Creating invitation with data:", {
        name: invitationData.name,
        eventId: invitationData.eventId,
        published: invitationData.published
      });
      
      // Remove id if it's null to let Firestore generate one
      const { id, ...dataWithoutId } = invitationData;
      
      // Store the eventId as a string for consistent querying
      const dataToSave = {
        ...dataWithoutId,
        eventId: String(dataWithoutId.eventId),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      console.log("🔍 Saving to Firestore with eventId:", dataToSave.eventId);
      const invitationRef = await addDoc(collection(db, 'invitations'), dataToSave);
      
      // Verify the saved data
      const savedDoc = await getDoc(invitationRef);
      console.log("🔍 Saved invitation with data:", savedDoc.data());
      
      return {
        id: invitationRef.id,
        ...dataToSave
      };
    } catch (error) {
      console.error("Error creating invitation:", error);
      throw error;
    }
  },
  
  async update(id, invitationData) {
    try {
      const invitationRef = doc(db, 'invitations', id);
      
      await updateDoc(invitationRef, {
        ...invitationData,
        updatedAt: serverTimestamp()
      });
      
      return {
        id,
        ...invitationData
      };
    } catch (error) {
      console.error("Error updating invitation:", error);
      throw error;
    }
  },
  
  async delete(id) {
    try {
      // First get the invitation to find all image paths
      const invitation = await this.getById(id);
      
      // Delete all associated images from storage
      if (invitation && invitation.elements) {
        const deletePromises = invitation.elements
          .filter(element => element.type === 'image' && element.storagePath)
          .map(element => deleteFile(element.storagePath));
          
        await Promise.all(deletePromises);
      }
      
      // Delete the invitation document
      await deleteDoc(doc(db, 'invitations', id));
      return true;
    } catch (error) {
      console.error("Error deleting invitation:", error);
      throw error;
    }
  },
  
  async updateElement(invitationId, elementId, elementData) {
    try {
      // If replacing an image, delete the old one
      const invitation = await this.getById(invitationId);
      const oldElement = invitation.elements.find(element => element.id === elementId);
      
      if (oldElement && 
          oldElement.type === 'image' && 
          oldElement.storagePath &&
          elementData.storagePath !== oldElement.storagePath) {
        await deleteFile(oldElement.storagePath);
      }
      
      // Update the element
      // ... existing update logic
    } catch (error) {
      console.error("Error updating element:", error);
      throw error;
    }
  }
};

// Template functions
export const templateService = {
  async getAll(userId) {
    try {
      // First get user's personal templates
      const templatesRef = collection(db, 'templates');
      const q = query(templatesRef, where('userId', '==', userId));
      
      const querySnapshot = await getDocs(q);
      const userTemplates = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Then get all published templates from admin users
      const publishedTemplatesQuery = query(
        templatesRef, 
        where('isPublished', '==', true)
      );
      
      const publishedSnapshot = await getDocs(publishedTemplatesQuery);
      const publishedTemplates = publishedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        isAdminTemplate: true // Mark as admin template for UI handling
      }));
      
      // Combine both sets of templates, removing duplicates
      // If a user has their own version and there's a published version with the same ID,
      // prefer the user's own version
      const allTemplateIds = new Set();
      const combinedTemplates = [...userTemplates];
      
      userTemplates.forEach(template => {
        allTemplateIds.add(template.id);
      });
      
      // Add published templates that the user doesn't already have
      publishedTemplates.forEach(template => {
        if (!allTemplateIds.has(template.id)) {
          combinedTemplates.push(template);
        }
      });
      
      return combinedTemplates;
    } catch (error) {
      console.error("Error getting templates:", error);
      throw error;
    }
  },
  
  async getById(id) {
    try {
      const templateDoc = await getDoc(doc(db, 'templates', id));
      
      if (templateDoc.exists()) {
        return {
          id: templateDoc.id,
          ...templateDoc.data()
        };
      }
      return null;
    } catch (error) {
      console.error("Error getting template:", error);
      throw error;
    }
  },
  
  async create(templateData) {
    try {
      console.log("🔄 [TemplateService] Create template called:", {
        templateName: templateData.name,
        userId: templateData.userId,
        hasThumbnail: !!templateData.thumbnail,
        thumbnailLength: templateData.thumbnail?.length
      });
      
      // Check if the current user is an admin
      const userDoc = await getDoc(doc(db, 'users', templateData.userId));
      const isAdmin = userDoc.exists() && userDoc.data().isAdmin === true;
      console.log("👤 [TemplateService] User admin status:", isAdmin);

      // First create the template document to get an ID
      const templateRef = await addDoc(collection(db, 'templates'), {
        ...templateData,
        thumbnail: null, // We'll update this after uploading the thumbnail
        thumbnailPath: null,
        isAdmin,
        isPublished: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      const templateId = templateRef.id;
      console.log("✅ [TemplateService] Template document created with ID:", templateId);
      
      // Process thumbnail if provided
      let thumbnail = null;
      let thumbnailPath = null;
      
      if (templateData.thumbnail) {
        try {
          console.log("🖼️ [TemplateService] Processing thumbnail for upload:", {
            type: typeof templateData.thumbnail,
            length: templateData.thumbnail.length,
            isDataUrl: typeof templateData.thumbnail === 'string' && templateData.thumbnail.startsWith('data:image/')
          });
          
          // Check if it's a valid data URL
          if (typeof templateData.thumbnail === 'string' && templateData.thumbnail.startsWith('data:image/')) {
            console.log("⬆️ [TemplateService] Starting thumbnail upload for template:", templateId);
            
            // Use the uploadTemplateThumbnail function which handles folder creation
            try {
              const result = await uploadTemplateThumbnail(
                templateData.thumbnail,
                templateData.userId,
                templateId
              );
              
              thumbnail = result.url;
              thumbnailPath = result.path;
              
              console.log("✅ [TemplateService] Template thumbnail uploaded successfully:", {
                url: thumbnail,
                path: thumbnailPath
              });
              
              // Update the template with the thumbnail URL and path
              try {
                await updateDoc(templateRef, { 
                  thumbnail,
                  thumbnailPath
                });
                console.log("✅ [TemplateService] Template document updated with thumbnail info");
              } catch (updateError) {
                console.error("❌ [TemplateService] Failed to update template with thumbnail:", updateError);
              }
            } catch (uploadError) {
              console.error("❌ [TemplateService] Thumbnail upload failed:", uploadError);
            }
          } else {
            console.warn("⚠️ [TemplateService] Invalid thumbnail format, not uploading");
          }
        } catch (error) {
          console.error('❌ [TemplateService] Error handling template thumbnail:', error);
        }
      } else {
        console.log("ℹ️ [TemplateService] No thumbnail provided for template");
      }
      
      const result = {
        id: templateId,
        ...templateData,
        thumbnail,
        thumbnailPath,
        isAdmin,
        isPublished: false
      };
      
      console.log("🎉 [TemplateService] Template creation completed:", {
        id: result.id,
        hasThumbnail: !!result.thumbnail,
        thumbnailPath: result.thumbnailPath
      });
      
      return result;
    } catch (error) {
      console.error("❌ [TemplateService] Error creating template:", error);
      throw error;
    }
  },
  
  async update(id, templateData) {
    try {
      console.log("🔄 [TemplateService] Update template called:", {
        templateId: id,
        templateName: templateData.name,
        userId: templateData.userId,
        hasThumbnail: !!templateData.thumbnail,
        thumbnailIsSame: templateData.thumbnail === templateData.existingThumbnail
      });
      
      // Get existing template to check permissions
      const templateDoc = await getDoc(doc(db, 'templates', id));
      
      if (!templateDoc.exists()) {
        console.error("❌ [TemplateService] Template not found:", id);
        throw new Error("Template not found");
      }
      
      const existingTemplate = {
        id: templateDoc.id,
        ...templateDoc.data()
      };
      
      console.log("ℹ️ [TemplateService] Existing template details:", {
        id: existingTemplate.id,
        name: existingTemplate.name,
        isPublished: existingTemplate.isPublished,
        ownerId: existingTemplate.userId,
        currentUserId: templateData.userId,
        hasThumbnail: !!existingTemplate.thumbnail,
        hasThumbnailPath: !!existingTemplate.thumbnailPath,
        thumbnailPath: existingTemplate.thumbnailPath
      });
      
      // Get user details to check if they're an admin
      const userDoc = await getDoc(doc(db, 'users', templateData.userId));
      const isAdmin = userDoc.exists() && userDoc.data().isAdmin === true;
      console.log("👤 [TemplateService] User admin status:", isAdmin);

      // Check if this is a published template and the user is not an admin
      // OR if the template belongs to another user
      if ((existingTemplate.isPublished && !isAdmin) || 
          (existingTemplate.userId !== templateData.userId && !isAdmin)) {
        // Create a new template instead of updating the published one
        console.log("🔀 [TemplateService] Non-admin user attempting to modify a published or other user's template");
        console.log("➕ [TemplateService] Creating a copy of the template for this user");
        
        // Prepare new template data
        const newTemplateData = {
          ...templateData,
          name: templateData.name || `Copy of ${existingTemplate.name}`,
          userId: templateData.userId,
          isPublished: false, // New copy is not published
          createdAt: serverTimestamp(),
          copiedFrom: id // Add reference to the original template
        };
        
        // Process thumbnail
        if (templateData.thumbnail) {
          // If user provided a new thumbnail, use it
          console.log("🖼️ [TemplateService] Using new thumbnail for the template copy");
        } else if (existingTemplate.thumbnail) {
          // Copy the existing thumbnail
          console.log("🖼️ [TemplateService] Copying original template's thumbnail");
          newTemplateData.thumbnail = existingTemplate.thumbnail;
          // Don't copy thumbnailPath as we'll generate a new one
        }
        
        // Create the new template
        const result = await this.create(newTemplateData);
        console.log("✅ [TemplateService] Created new template copy:", result.id);
        
        // Return the newly created template with a special flag indicating this was a copy
        return {
          ...result,
          wasCopied: true
        };
      }
      
      // Process thumbnail if provided
      let thumbnail = existingTemplate.thumbnail;
      let thumbnailPath = existingTemplate.thumbnailPath;
      
      if (templateData.thumbnail && templateData.thumbnail !== existingTemplate.thumbnail) {
        try {
          console.log("🖼️ [TemplateService] New thumbnail provided, processing for update:", {
            isDataUrl: typeof templateData.thumbnail === 'string' && templateData.thumbnail.startsWith('data:image/'),
            length: templateData.thumbnail.length
          });
          
          // Check if it's a valid data URL
          if (typeof templateData.thumbnail === 'string' && templateData.thumbnail.startsWith('data:image/')) {
            console.log("⬆️ [TemplateService] Starting thumbnail upload for template update:", id);
            
            // Use the uploadTemplateThumbnail function which handles folder creation
            try {
              const result = await uploadTemplateThumbnail(
                templateData.thumbnail,
                templateData.userId,
                id
              );
              
              thumbnail = result.url;
              thumbnailPath = result.path;
              
              console.log("✅ [TemplateService] Template thumbnail updated successfully:", {
                url: thumbnail,
                path: thumbnailPath
              });
  
              // Delete old thumbnail if it exists and is different from the new one
              if (existingTemplate.thumbnail && existingTemplate.thumbnailPath && 
                  existingTemplate.thumbnailPath !== thumbnailPath) {
                try {
                  console.log("🗑️ [TemplateService] Deleting old thumbnail:", existingTemplate.thumbnailPath);
                  await deleteFile(existingTemplate.thumbnailPath);
                  console.log("✅ [TemplateService] Old thumbnail deleted successfully");
                } catch (deleteError) {
                  console.error('⚠️ [TemplateService] Unable to delete old thumbnail:', deleteError);
                  // Continue anyway, this is just cleanup
                }
              }
            } catch (uploadError) {
              console.error("❌ [TemplateService] Thumbnail upload failed during update:", uploadError);
            }
          } else {
            console.warn("⚠️ [TemplateService] Invalid thumbnail format during update, not uploading");
          }
        } catch (error) {
          console.error('❌ [TemplateService] Error handling template thumbnail during update:', error);
        }
      } else if (templateData.thumbnail === undefined || templateData.thumbnail === null) {
        // If thumbnail is undefined or null, keep the existing one
        console.log('ℹ️ [TemplateService] No new thumbnail provided, keeping existing:', {
          existingThumbnail: !!thumbnail,
          existingPath: thumbnailPath
        });
      } else {
        console.log('ℹ️ [TemplateService] Thumbnail unchanged during update');
      }

      // Only allow publishing if user is admin
      let isPublished = existingTemplate.isPublished || false;
      if (templateData.isPublished !== undefined) {
        if (isAdmin) {
          isPublished = templateData.isPublished;
          console.log("🔄 [TemplateService] Admin updating publish status:", isPublished);
        } else {
          console.warn("⚠️ [TemplateService] Non-admin user attempted to publish a template");
        }
      }
      
      // Regular update for owners or admins
      const templateRef = doc(db, 'templates', id);
      console.log("⬆️ [TemplateService] Updating template document with:", {
        id,
        thumbnail: !!thumbnail,
        thumbnailPath,
        isPublished
      });
      
      await updateDoc(templateRef, {
        ...templateData,
        thumbnail,
        thumbnailPath,
        isPublished,
        updatedAt: serverTimestamp()
      });
      
      const result = {
        id,
        ...templateData,
        thumbnail,
        thumbnailPath,
        isPublished
      };
      
      console.log("🎉 [TemplateService] Template update completed:", {
        id: result.id,
        hasThumbnail: !!result.thumbnail,
        thumbnailPath: result.thumbnailPath
      });
      
      return result;
    } catch (error) {
      console.error("❌ [TemplateService] Error updating template:", error);
      throw error;
    }
  },
  
  async delete(id) {
    try {
      // Get the template to check ownership
      const templateDoc = await getDoc(doc(db, 'templates', id));
      
      if (!templateDoc.exists()) {
        throw new Error("Template not found");
      }
      
      const template = templateDoc.data();
      const currentUser = auth.currentUser;
      
      // Only allow deletion if:
      // 1. User is the owner, OR
      // 2. User is an admin
      if (template.userId !== currentUser.uid) {
        // Check if user is admin
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const isAdmin = userDoc.exists() && userDoc.data().isAdmin === true;
        
        if (!isAdmin) {
          throw new Error("You don't have permission to delete this template");
        }
      }
      
      // If we got here, user has permission to delete
      
      // Delete thumbnail if it exists
      if (template.thumbnail) {
        try {
          console.log("Deleting template thumbnail...");
          
          // Use the thumbnailPath if available (preferred method)
          if (template.thumbnailPath) {
            await deleteFile(template.thumbnailPath);
            console.log("Thumbnail deleted successfully using stored path:", template.thumbnailPath);
          } 
          // For legacy thumbnails, try to parse the URL
          else {
            console.log("No thumbnailPath found in template. Using legacy URL parsing for cleanup");
            const thumbnailUrl = template.thumbnail;
            
            try {
              // Try to extract the path from Firebase URL
              if (thumbnailUrl && thumbnailUrl.includes('firebase')) {
                // Special handling for Firebase Storage URLs
                const pathRegex = /users\/.+\/templates\/.+\/thumbnail\.png/;
                const match = thumbnailUrl.match(pathRegex);
                
                if (match && match[0]) {
                  await deleteFile(match[0]);
                  console.log("Legacy thumbnail deleted successfully using extracted path:", match[0]);
                } else {
                  console.log("Could not extract path from URL:", thumbnailUrl);
                }
              }
            } catch (error) {
              console.error("Error cleaning up legacy thumbnail:", error);
              // Continue with template deletion
            }
          }
        } catch (error) {
          console.error("Error during thumbnail deletion:", error);
          // Continue with template deletion even if thumbnail deletion fails
        }
      }
      
      // Delete the template document
      await deleteDoc(doc(db, 'templates', id));
      return true;
    } catch (error) {
      console.error("Error deleting template:", error);
      throw error;
    }
  },
  
  async getAllTemplates() {
    try {
      const querySnapshot = await getDocs(collection(db, 'templates'));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error("Error getting all templates:", error);
      throw error;
    }
  },
  
  async getPublishedTemplates() {
    try {
      const templatesRef = collection(db, 'templates');
      const q = query(templatesRef, where('isPublished', '==', true));
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        isAdminTemplate: true // Mark as admin template for UI handling
      }));
    } catch (error) {
      console.error("Error getting published templates:", error);
      throw error;
    }
  },
  
  async publishTemplate(id, shouldPublish = true) {
    try {
      // Check if user is admin
      const currentUser = auth.currentUser;
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const isAdmin = userDoc.exists() && userDoc.data().isAdmin === true;
      
      if (!isAdmin) {
        throw new Error("Only administrators can publish templates");
      }
      
      // Update the publish status
      const templateRef = doc(db, 'templates', id);
      await updateDoc(templateRef, {
        isPublished: shouldPublish,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error("Error publishing template:", error);
      throw error;
    }
  }
};

// Guest functions
export const guestService = {
  async getByEventId(eventId) {
    try {
      const guestsRef = collection(db, 'guests');
      const q = query(guestsRef, where('eventId', '==', eventId));
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error("Error getting guests:", error);
      throw error;
    }
  },
  
  async getById(id) {
    try {
      const guestDoc = await getDoc(doc(db, 'guests', id));
      
      if (guestDoc.exists()) {
        return {
          id: guestDoc.id,
          ...guestDoc.data()
        };
      }
      return null;
    } catch (error) {
      console.error("Error getting guest:", error);
      throw error;
    }
  },
  
  async create(guestData) {
    try {
      const guestRef = await addDoc(collection(db, 'guests'), {
        ...guestData,
        createdAt: serverTimestamp()
      });
      
      return {
        id: guestRef.id,
        ...guestData
      };
    } catch (error) {
      console.error("Error creating guest:", error);
      throw error;
    }
  },
  
  async update(id, guestData) {
    try {
      const guestRef = doc(db, 'guests', id);
      
      await updateDoc(guestRef, guestData);
      
      return {
        id,
        ...guestData
      };
    } catch (error) {
      console.error("Error updating guest:", error);
      throw error;
    }
  },
  
  async delete(id) {
    try {
      await deleteDoc(doc(db, 'guests', id));
      return true;
    } catch (error) {
      console.error("Error deleting guest:", error);
      throw error;
    }
  },
  
  async getByEmail(email) {
    try {
      console.log("Getting guests with email:", email);
      
      const guestsRef = collection(db, 'guests');
      const q = query(guestsRef, where('email', '==', email));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error("Error getting guests by email:", error);
      return [];
    }
  },
  
  async getByEmailAndEvent(email, eventId) {
    try {
      console.log("Getting guests with email and event:", email, eventId);
      
      const guestsRef = collection(db, 'guests');
      const q = query(
        guestsRef, 
        where('email', '==', email),
        where('eventId', '==', eventId)
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error("Error getting guests by email and event:", error);
      return [];
    }
  }
};
